import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import ReactDOM from "react-dom";

import { faBracketsCurly, faSearch } from "@fortawesome/pro-regular-svg-icons";

import { useTranslation } from "react-i18next";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { classNames } from "@/utils/classNames";
import { useViewportClampedPanel } from "@/utils/useViewportClampedPanel";
import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

export interface VariableOption {
	key: string;
	label: string;
}

interface VariablePickerProps {
	variables: VariableOption[];
	/** Called with the raw variable key (without braces). The caller wraps it as `{key}`. */
	onInsert: (key: string) => void;
	/** Portal target (extension passes its shadow root); defaults to `document.body`. */
	portalContainer?: HTMLElement | null;
}

const ESTIMATED_MENU_HEIGHT = 320;

/**
 * Lightweight "Insert variable" dropdown for the inbox chat composer. Mirrors
 * the portal pattern of `SenderRecipientDropdown` (rendered into `document.body`
 * so it is not clipped by the chat's scroll containers — see CLAUDE.md rule 11)
 * and adds a search field over the available variables. Selecting a variable
 * inserts its raw `{token}` into the plain-text draft.
 */
export const VariablePicker: FunctionComponent<VariablePickerProps> = ({
	variables,
	onInsert,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const [search, setSearch] = useState("");
	const triggerRef = useRef<HTMLDivElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	// Keeps the portalled panel inside the viewport wherever the trigger sits (PREC-4138).
	useViewportClampedPanel(menuRef, isOpen);
	const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

	const updatePosition = useCallback(() => {
		if (!triggerRef.current) {
			return;
		}

		const rect = triggerRef.current.getBoundingClientRect();
		const spaceBelow = window.innerHeight - rect.bottom;
		const openUpward = spaceBelow < ESTIMATED_MENU_HEIGHT && rect.top > spaceBelow;

		if (openUpward) {
			setMenuStyle({
				position: "fixed",
				bottom: window.innerHeight - rect.top + 4,
				left: rect.left,
				maxHeight: Math.min(rect.top - 8, ESTIMATED_MENU_HEIGHT),
			});
		} else {
			setMenuStyle({
				position: "fixed",
				top: rect.bottom + 4,
				left: rect.left,
				maxHeight: Math.min(spaceBelow - 8, ESTIMATED_MENU_HEIGHT),
			});
		}
	}, []);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);

			if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) {
				return;
			}

			setIsOpen(false);
		};

		if (isOpen) {
			updatePosition();
			document.addEventListener("mousedown", handleClickOutside);
			window.addEventListener("resize", updatePosition);
			window.addEventListener("scroll", updatePosition, true);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			window.removeEventListener("resize", updatePosition);
			window.removeEventListener("scroll", updatePosition, true);
		};
	}, [isOpen, updatePosition]);

	const filteredVariables = useMemo(() => {
		const query = search.trim().toLowerCase();

		if (!query) {
			return variables;
		}

		return variables.filter(
			(v) => v.label.toLowerCase().includes(query) || v.key.toLowerCase().includes(query)
		);
	}, [variables, search]);

	const handleSelect = useCallback(
		(key: string) => {
			onInsert(key);
			setIsOpen(false);
			setSearch("");
		},
		[onInsert]
	);

	const menu = useMemo(() => {
		if (!isOpen) {
			return null;
		}

		return ReactDOM.createPortal(
			<div
				ref={menuRef}
				className="bg-white border border-border-light rounded-lg shadow-lg py-1 w-[260px] flex flex-col z-[9999]"
				style={menuStyle}
			>
				<div className="px-2 py-1.5 border-b border-border-light">
					<div className="relative">
						<Icon
							className="w-3 h-3 text-text-meta absolute left-2 top-1/2 -translate-y-1/2"
							icon={faSearch}
						/>
						<input
							autoFocus
							className="w-full text-xs bg-transparent border border-border-light rounded pl-7 pr-2 py-1 outline-none focus:border-blue-action"
							data-testid="variable-search"
							placeholder={t("inbox.variable.search")}
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>
					</div>
				</div>
				<div className="overflow-y-auto py-1">
					{filteredVariables.length === 0 ? (
						<div className="px-3 py-2 text-xs text-text-meta">{t("inbox.variable.noResults")}</div>
					) : (
						filteredVariables.map((v) => (
							<button
								key={v.key}
								className={classNames(
									"w-full px-3 py-1.5 text-left text-xs flex flex-col hover:bg-brand-white"
								)}
								onClick={(e) => {
									e.stopPropagation();
									handleSelect(v.key);
								}}
							>
								<span className="text-text truncate">{v.label}</span>
								<span className="text-[10px] text-text-meta truncate">{`{${v.key}}`}</span>
							</button>
						))
					)}
				</div>
			</div>,
			portalContainer ?? document.body
		);
	}, [isOpen, menuStyle, filteredVariables, search, handleSelect, t, portalContainer]);

	return (
		<div ref={triggerRef} className="w-fit">
			<Button
				color={ButtonColor.ACTION_SECONDARY}
				image={<Icon className="mr-2" icon={faBracketsCurly} />}
				size={ButtonSize.XS}
				title={t("inbox.variable.insert")}
				onClick={() => setIsOpen((prev) => !prev)}
			/>
			{menu}
		</div>
	);
};
